import { useState } from "react";
import { Link, useLocation } from "@tanstack/react-router";
import {
  LayoutDashboard,
  Calendar,
  Users,
  Settings,
  BarChart3,
  FileText,
  LogOut,
  Menu,
  X,
  Plus,
  Edit,
  Trash2,
  Eye,
  UserCheck,
} from "lucide-react";
import { useAuthStore } from "../../store/authStore";
import { useAuthService } from "../../services/authService";
import OrganizationEventForm from "../protected/organization-events/OrganizationEventForm";
import {
  IOrganizationEvent,
  ICreateOrganizationEventPayload,
} from "../../types/EventTypes";
import { V1BEClient } from "../../clients";

const AdminSidebar = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<IOrganizationEvent | null>(
    null
  );
  const { userDetails } = useAuthStore();
  const { handleLogout: logout } = useAuthService();
  const location = useLocation();

  const isAdmin =
    (userDetails?.user_level || 0) >= 99 ||
    userDetails?.permissions?.includes("*") ||
    userDetails?.permissions?.includes("event.manage");

  const navigation = isAdmin
    ? [
        { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
        { name: "Events", href: "/admin/events", icon: Calendar },
        { name: "Attendees", href: "/admin/attendees", icon: UserCheck },
        // { name: "Users", href: "/admin/users", icon: Users },
        // { name: "Reports", href: "/admin/reports", icon: FileText },
      ]
    : [{ name: "Events", href: "/event", icon: Calendar }];

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleLogoutClick = () => {
    logout();
    setIsSidebarOpen(false);
  };

  const handleCreateEvent = () => {
    setEditingEvent(null);
    setShowEventForm(true);
    setIsSidebarOpen(false);
  };

  const handleSaveEvent = async (
    eventData: ICreateOrganizationEventPayload
  ) => {
    try {
      if (editingEvent) {
        // Update existing event
        const success = await V1BEClient.updateOrganizationEvent(eventData);
        if (!success) {
          throw new Error("Failed to update event");
        }
      } else {
        // Create new event
        const newEvent = await V1BEClient.createOrganizationEvent(eventData);
        if (!newEvent) {
          throw new Error("Failed to create event");
        }
      }
      setShowEventForm(false);
      setEditingEvent(null);
    } catch (error) {
      console.error("Error saving event:", error);
      throw error;
    }
  };

  const handleCloseEventForm = () => {
    setShowEventForm(false);
    setEditingEvent(null);
  };

  const isActive = (href: string) => {
    return location.pathname === href;
  };

  return (
    <>
      {/* Mobile menu button */}
      <button
        onClick={toggleSidebar}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-lg"
      >
        {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar */}
      <div
        className={`
        fixed inset-y-0 left-0 z-40 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0
        ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}
      `}
      >
        {/* Sidebar Header */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">P</span>
            </div>
            <span className="text-xl font-bold text-gray-900">PhilTrade</span>
          </div>
          <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full font-medium">
            Admin
          </span>
        </div>

        {/* User Info */}
        <Link to="/profile">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <span className="text-red-600 font-bold">
                  {userDetails?.firstName?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {userDetails?.firstName} {userDetails?.lastName}
                </p>
                <p className="text-xs text-gray-500">{userDetails?.email}</p>
              </div>
            </div>
          </div>
        </Link>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-2">
          {navigation.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`
                  flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200
                  ${
                    isActive(item.href)
                      ? "bg-red-50 text-red-700 border-r-2 border-red-600"
                      : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                  }
                `}
                onClick={() => setIsSidebarOpen(false)}
              >
                <Icon size={20} />
                <span>{item.name}</span>
              </Link>
            );
          })}
        </nav>

        {/* Quick Actions */}
        {isAdmin && (
          <div className="px-4 py-4 border-t border-gray-200">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Quick Actions
            </h3>
            <div className="space-y-2">
              <button
                className="flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors duration-200 w-full"
                onClick={handleCreateEvent}
              >
                <Plus size={20} />
                <span>Create Event</span>
              </button>
            </div>
          </div>
        )}

        {/* Logout */}
        <div className="px-4 py-4 border-t border-gray-200">
          <button
            onClick={handleLogoutClick}
            className="flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors duration-200 w-full"
          >
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </div>

      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Event Form Dialog */}
      {showEventForm && (
        <OrganizationEventForm
          event={editingEvent}
          onSave={handleSaveEvent}
          onClose={handleCloseEventForm}
        />
      )}
    </>
  );
};

export default AdminSidebar;
